export type ChannelPaymentProofStatus = 'pending' | 'resubmission_required' | 'approved';

export interface ChannelPaymentProofRecord {
  readonly recordId: string;
  readonly evidenceRef: string;
  readonly submittedAt: string;
  readonly submittedBy: string;
}

export interface ChannelPaymentProofSnapshot {
  readonly submissionId: string;
  readonly version: number;
  readonly status: ChannelPaymentProofStatus;
  readonly currentEvidenceRef: string;
  readonly records: readonly ChannelPaymentProofRecord[];
  readonly reviewReason: string | null;
  readonly reviewedAt: string | null;
  readonly reviewedBy: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: string | null;
}

export interface SubmitInitialPaymentProofCommand {
  readonly submissionId: string;
  readonly evidenceRef: string;
  readonly submittedAt: string;
  readonly submittedBy: string;
}

export interface RequestPaymentProofResubmissionCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly actorId: string;
  readonly at: string;
  readonly reason: string;
}

export interface ResubmitPaymentProofCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly evidenceRef: string;
  readonly submittedAt: string;
  readonly submittedBy: string;
}

export interface ApprovePaymentProofCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly actorId: string;
  readonly at: string;
}

type IdempotentOperation = {
  readonly signature: string;
  readonly snapshot: ChannelPaymentProofSnapshot;
};

const clone = <T>(value: T): T => structuredClone(value);

const assertText = (value: string, code: string): string => {
  const normalized = value?.trim();
  if (!normalized) throw new Error(code);
  return normalized;
};

const assertTimestamp = (value: string, code: string): string => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
  return value;
};

const assertVersion = (value: number): void => {
  if (!Number.isSafeInteger(value) || value < 1) throw new Error('PAYMENT_PROOF_VERSION_INVALID');
};

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

export function createChannelPaymentProofReview() {
  const snapshots = new Map<string, ChannelPaymentProofSnapshot>();
  const idempotency = new Map<string, IdempotentOperation>();

  const requireSnapshot = (submissionId: string): ChannelPaymentProofSnapshot => {
    const normalized = assertText(submissionId, 'PAYMENT_PROOF_SUBMISSION_ID_REQUIRED');
    const snapshot = snapshots.get(normalized);
    if (!snapshot) throw new Error('PAYMENT_PROOF_NOT_FOUND');
    return snapshot;
  };

  const commitIdempotent = (
    idempotencyKey: string,
    signature: string,
    action: () => ChannelPaymentProofSnapshot,
  ): ChannelPaymentProofSnapshot => {
    const key = assertText(idempotencyKey, 'PAYMENT_PROOF_IDEMPOTENCY_REQUIRED');
    const existing = idempotency.get(key);
    if (existing) {
      if (existing.signature !== signature) throw new Error('PAYMENT_PROOF_IDEMPOTENCY_CONFLICT');
      return clone(existing.snapshot);
    }
    const snapshot = action();
    idempotency.set(key, {signature, snapshot: clone(snapshot)});
    return clone(snapshot);
  };

  const assertExpectedVersion = (snapshot: ChannelPaymentProofSnapshot, expectedVersion: number): void => {
    assertVersion(expectedVersion);
    if (snapshot.version !== expectedVersion) throw new Error('PAYMENT_PROOF_VERSION_CONFLICT');
  };

  return {
    submitInitial(command: SubmitInitialPaymentProofCommand): ChannelPaymentProofSnapshot {
      const submissionId = assertText(command.submissionId, 'PAYMENT_PROOF_SUBMISSION_ID_REQUIRED');
      const evidenceRef = assertText(command.evidenceRef, 'PAYMENT_PROOF_EVIDENCE_REF_REQUIRED');
      const submittedBy = assertText(command.submittedBy, 'PAYMENT_PROOF_SUBMITTED_BY_REQUIRED');
      const submittedAt = assertTimestamp(command.submittedAt, 'PAYMENT_PROOF_SUBMITTED_AT_INVALID');
      const existing = snapshots.get(submissionId);
      if (existing) {
        const first = existing.records[0];
        if (first?.evidenceRef === evidenceRef && first.submittedAt === submittedAt && first.submittedBy === submittedBy) {
          return clone(existing);
        }
        throw new Error('PAYMENT_PROOF_INITIAL_CONFLICT');
      }
      const firstRecord: ChannelPaymentProofRecord = {
        recordId: `payment-proof:${submissionId}:1`,
        evidenceRef,
        submittedAt,
        submittedBy,
      };
      const snapshot: ChannelPaymentProofSnapshot = {
        submissionId,
        version: 1,
        status: 'pending',
        currentEvidenceRef: evidenceRef,
        records: [firstRecord],
        reviewReason: null,
        reviewedAt: null,
        reviewedBy: null,
        approvedAt: null,
        approvedBy: null,
      };
      snapshots.set(submissionId, snapshot);
      return clone(snapshot);
    },

    requestResubmission(
      submissionId: string,
      command: RequestPaymentProofResubmissionCommand,
    ): ChannelPaymentProofSnapshot {
      const normalizedSubmissionId = assertText(submissionId, 'PAYMENT_PROOF_SUBMISSION_ID_REQUIRED');
      const actorId = assertText(command.actorId, 'PAYMENT_PROOF_REVIEWER_REQUIRED');
      const at = assertTimestamp(command.at, 'PAYMENT_PROOF_REVIEW_AT_INVALID');
      const reason = assertText(command.reason, 'PAYMENT_PROOF_REVIEW_REASON_REQUIRED');
      const signature = signatureOf({kind: 'request-resubmission', submissionId: normalizedSubmissionId, ...command, actorId, at, reason});
      return commitIdempotent(command.idempotencyKey, signature, () => {
        const current = requireSnapshot(normalizedSubmissionId);
        assertExpectedVersion(current, command.expectedVersion);
        if (current.status !== 'pending') throw new Error('PAYMENT_PROOF_NOT_PENDING');
        const next: ChannelPaymentProofSnapshot = {
          ...current,
          version: current.version + 1,
          status: 'resubmission_required',
          reviewReason: reason,
          reviewedAt: at,
          reviewedBy: actorId,
          approvedAt: null,
          approvedBy: null,
        };
        snapshots.set(normalizedSubmissionId, next);
        return next;
      });
    },

    resubmit(submissionId: string, command: ResubmitPaymentProofCommand): ChannelPaymentProofSnapshot {
      const normalizedSubmissionId = assertText(submissionId, 'PAYMENT_PROOF_SUBMISSION_ID_REQUIRED');
      const evidenceRef = assertText(command.evidenceRef, 'PAYMENT_PROOF_EVIDENCE_REF_REQUIRED');
      const submittedBy = assertText(command.submittedBy, 'PAYMENT_PROOF_SUBMITTED_BY_REQUIRED');
      const submittedAt = assertTimestamp(command.submittedAt, 'PAYMENT_PROOF_SUBMITTED_AT_INVALID');
      const signature = signatureOf({kind: 'resubmit', submissionId: normalizedSubmissionId, ...command, evidenceRef, submittedBy, submittedAt});
      return commitIdempotent(command.idempotencyKey, signature, () => {
        const current = requireSnapshot(normalizedSubmissionId);
        assertExpectedVersion(current, command.expectedVersion);
        if (current.status !== 'resubmission_required') throw new Error('PAYMENT_PROOF_RESUBMISSION_NOT_REQUIRED');
        const record: ChannelPaymentProofRecord = {
          recordId: `payment-proof:${normalizedSubmissionId}:${current.records.length + 1}`,
          evidenceRef,
          submittedAt,
          submittedBy,
        };
        const next: ChannelPaymentProofSnapshot = {
          ...current,
          version: current.version + 1,
          status: 'pending',
          currentEvidenceRef: evidenceRef,
          records: [...current.records, record],
          reviewReason: null,
          reviewedAt: null,
          reviewedBy: null,
          approvedAt: null,
          approvedBy: null,
        };
        snapshots.set(normalizedSubmissionId, next);
        return next;
      });
    },

    approve(submissionId: string, command: ApprovePaymentProofCommand): ChannelPaymentProofSnapshot {
      const normalizedSubmissionId = assertText(submissionId, 'PAYMENT_PROOF_SUBMISSION_ID_REQUIRED');
      const actorId = assertText(command.actorId, 'PAYMENT_PROOF_REVIEWER_REQUIRED');
      const at = assertTimestamp(command.at, 'PAYMENT_PROOF_REVIEW_AT_INVALID');
      const signature = signatureOf({kind: 'approve', submissionId: normalizedSubmissionId, ...command, actorId, at});
      return commitIdempotent(command.idempotencyKey, signature, () => {
        const current = requireSnapshot(normalizedSubmissionId);
        assertExpectedVersion(current, command.expectedVersion);
        if (current.status !== 'pending') throw new Error('PAYMENT_PROOF_NOT_PENDING');
        const next: ChannelPaymentProofSnapshot = {
          ...current,
          version: current.version + 1,
          status: 'approved',
          reviewReason: null,
          reviewedAt: at,
          reviewedBy: actorId,
          approvedAt: at,
          approvedBy: actorId,
        };
        snapshots.set(normalizedSubmissionId, next);
        return next;
      });
    },

    get(submissionId: string): ChannelPaymentProofSnapshot | null {
      const normalized = submissionId.trim();
      if (!normalized) return null;
      const snapshot = snapshots.get(normalized);
      return snapshot ? clone(snapshot) : null;
    },
  };
}
